import { NoteAdd as NoteAddIcon } from "@mui/icons-material";
import WarningIcon from "@mui/icons-material/Warning";
import { CircularProgress, Container, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { unwrapResult } from "@reduxjs/toolkit";
import { AsolviSwitch } from "components/AsolviSwitch";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import { EditPhoto } from "components/editPhoto/EditPhoto";
import { FilePreview } from "components/filePreview/FilePreview";
import FullScreenDialog from "components/FullScreenDialog";
import { PromptDialog } from "components/PromptDialog";
import { StyledFab } from "components/StyledFab";
import { isAbortError } from "helpers";
import { TabProps } from "models/TabProps";
import { VisitFile } from "models/visitFile";
import { nanoid } from "nanoid";
import { FileType } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import Resizer from "react-image-file-resizer";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import {
  addFile,
  getFiles,
  removeFile,
  selectSelectedJob,
  selectSelectedJobVisit,
  updateFile,
  uploadFile,
} from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";

const PREFIX = "FileTab";

const classes = {
  input: `${PREFIX}-input`,
};

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(2),

  [`& .${classes.input}`]: {
    display: "none",
  },
}));

export const FileTab: FC<TabProps> = (props) => {
  const { setFab } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { autoSyncDocuments } = useAppSelector((s) => s.user);
  const { fileUploading } = useAppSelector((s) => s.jobs);
  const { files } = useAppSelector(selectSelectedJobVisit);
  const { externalId, files: downloadedFiles, id: jobId } = useAppSelector(selectSelectedJob);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [removeId, setRemoveId] = useState<string | undefined>(undefined);
  const [openCloseConfirmation, setOpenCloseConfirmation] = useState(false);
  const [isFullSize, setIsFullSize] = useState(false);
  const [currentPhoto, setCurrentPhoto] = useState<FileType | undefined>();
  const [allFiles, setAllFiles] = useState<VisitFile[]>([]);

  const resizeFile = useCallback(
    (file: Blob) =>
      new Promise((resolve) => {
        if (file.type.includes("image/") && !isFullSize) {
          Resizer.imageFileResizer(
            file,
            600,
            600,
            "JPG",
            80,
            0,
            (uri) => {
              resolve(uri);
            },
            "base64",
          );
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(file);
        }
      }),
    [isFullSize],
  );

  const handleFileInput = useCallback(
    (e: any) => {
      const file = e.target.files[0];
      if (file) {
        resizeFile(file).then((data) => {
          const output = typeof data === "string" ? data : "";
          const f: FileType = {
            id: nanoid(),
            name: file.name,
            data: output,
            description: "",
            size: output.length,
            isFullSize: isFullSize,
            fileType: file.type,
          };
          if (autoSyncDocuments) {
            dispatch(uploadFile({ jobId, file: f }))
              .then(unwrapResult)
              .then(() => {
                dispatch(getFiles());
              })
              .catch((error: any) => {
                if (isAbortError(error)) return;
                dispatch(addSnackbarMessage({ key: "UploadFile-fail" }));
              });
          } else {
            dispatch(addFile({ file: f }));
          }
        });
      }
      e.target.value = null;
    },
    [autoSyncDocuments, dispatch, isFullSize, jobId, resizeFile],
  );

  const toggleFullSize = useCallback((value: boolean) => {
    console.log("toggling");
    localStorage.setItem("uncompressFiles", JSON.stringify(value));
    setIsFullSize(value);
  }, []);

  useEffect(() => {
    setAllFiles([
      ...downloadedFiles.map(
        (j) =>
          ({
            file: j,
            downloaded: true,
          }) as VisitFile,
      ),
      ...files.map(
        (j) =>
          ({
            file: j,
            downloaded: false,
          }) as VisitFile,
      ),
    ]);
  }, [downloadedFiles, files]);

  useEffect(() => {
    const fab = (
      <label htmlFor="upload-button-file">
        <StyledFab color="primary" component="span">
          <NoteAddIcon />
        </StyledFab>
      </label>
    );
    if (setFab) {
      setFab(fab);
    }
  }, [setFab]);

  useEffect(() => {
    setIsFullSize(JSON.parse(localStorage.getItem("uncompressFiles") ?? "false") || false);
  }, []);

  return (
    <StyledContainer data-testid="FileTab">
      <BackdropPrimaryMain open={fileUploading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ paddingTop: "20px", paddingBottom: "20px" }}
      >
        <Grid item>
          <Typography
            variant="body1"
            onClick={() => {
              toggleFullSize(!isFullSize);
            }}
          >
            <FormattedMessage id="file.uncompressImages" />
          </Typography>
        </Grid>
        <Grid item>
          <AsolviSwitch
            key="FullSizeSwitch"
            checked={isFullSize}
            onChange={() => {
              toggleFullSize(!isFullSize);
            }}
            data-testid="FileTab-FullSize-Toggle"
          />
        </Grid>
      </Grid>

      {isFullSize && (
        <Grid container sx={{ mb: 2 }}>
          <Grid item>
            <Typography>
              <WarningIcon color="warning" sx={{ mr: 1.5, mb: -0.5 }} fontSize="medium" />
              <FormattedMessage id="file.fullSizeWarningText" />
            </Typography>
          </Grid>
        </Grid>
      )}

      {allFiles.length === 0 && (
        <Grid container direction="column" alignItems="center">
          <Grid item>
            <NoteAddIcon color="secondary" fontSize="large" />
          </Grid>
          <Grid item>
            <Typography color="secondary">
              <FormattedMessage id="file.noFilesSelected" />
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={1}>
        {allFiles.map(({ file, downloaded }, index) => (
          <FilePreview
            key={index}
            file={file}
            onDelete={() => {
              setRemoveId(file.id!);
              setOpenRemoveDialog(true);
            }}
            onEdit={() => {
              setCurrentPhoto(file);
              setOpenEditDialog(true);
            }}
            data-testid={`FilePreview-${index}`}
            disableEdit={downloaded || file.isFullSize || false}
            downloadOnCardClick={downloaded}
            readonly={downloaded}
          />
        ))}
      </Grid>
      <input
        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
        className={classes.input}
        id="upload-button-file"
        type="file"
        data-testid="FileTab-FileInput"
        onChange={handleFileInput}
      />
      <FullScreenDialog
        open={openEditDialog}
        setOpen={(open: boolean) => setOpenCloseConfirmation(!open)}
        title={`${externalId} / ${intl.formatMessage({
          id: "file.editImage",
        })}`}
        centerTitle
        goingBack
        child={
          <EditPhoto
            setOpenEditDialog={setOpenEditDialog}
            currentPhoto={currentPhoto}
            onSave={(file) => {
              dispatch(updateFile({ file }));
            }}
            openCloseConfirmation={openCloseConfirmation}
            setOpenCloseConfirmation={setOpenCloseConfirmation}
          />
        }
      />
      <PromptDialog
        aria-labelledby="form-dialog-title"
        open={openRemoveDialog}
        setOpen={setOpenRemoveDialog}
        okText={intl.formatMessage({ id: "general.confirm" })}
        onOk={() => dispatch(removeFile({ id: removeId! }))}
        onCancel={() => setOpenRemoveDialog(false)}
        title={<FormattedMessage id="file.remove" />}
        promptContent={<FormattedMessage id="visit.file.removePrompt" />}
      />
    </StyledContainer>
  );
};
